import { APP_CONSTANTS } from "@core/constants/app-constants";
import { useI18n } from "@core/i18n/i18n";
import { NewTemplate, Template } from "@domain/template/template";
import { e2eTestId } from "@e2e/test-ids";
import styled from "@emotion/styled";
import { Button } from "@ui/components/buttons/button";
import { BaseDialog, DIALOG_SIZE } from "@ui/components/dialog/base-dialog";
import { TextAreaWithCharLimit } from "@ui/components/common/text-area-with-char-limit";
import { row, stack } from "@ui/style";
import { motion } from "framer-motion";
import { DialogProps } from "primereact/dialog";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";

interface FormData {
	name: string;
}

export interface TemplateNameDialogProps extends DialogProps {
	template: Template | NewTemplate;
	onSubmit: (name: string) => Promise<void>;
}

export const TemplateNameDialog: React.FC<TemplateNameDialogProps> = ({ template, onSubmit, ...otherProps }) => {
	const { format } = useI18n();
	const [loadingSubmit, setLoadingSubmit] = useState(false);

	const { control, handleSubmit } = useForm<FormData>({
		defaultValues: { name: template.name },
	});

	const saveTemplate = async ({ name }: FormData) => {
		setLoadingSubmit(true);
		await onSubmit(name);
		setLoadingSubmit(false);
		otherProps.onHide();
	};

	return (
		<BaseDialog
			header={format("templateBuilder.templateNameDialog.title.v2_6")}
			footer={
				<ButtonContainer>
					<Button
						label={format("templateBuilder.templateNameDialog.saveButton.v2_6")}
						data-test={e2eTestId.createTemplatePage.confirmButton}
						loading={loadingSubmit}
						type="submit"
						onClick={handleSubmit(saveTemplate)}
					/>
				</ButtonContainer>
			}
			width={DIALOG_SIZE.S}
			{...otherProps}
		>
			<DialogContainer>
				<p className="p-size-m">{format("templateBuilder.templateNameDialog.description")}</p>
				<Form onSubmit={handleSubmit(saveTemplate)}>
					<FormField>
						<label className="p-size-m">{format("templateBuilder.templateNameDialog.nameInput.label")}</label>
						<Controller
							name="name"
							control={control}
							rules={{
								required: format("global.form.field.requiredError"),
								maxLength: {
									value: APP_CONSTANTS.PROGRAM_OR_TEMPLATE_MAX_TITLE_LENGTH,
									message: format("global.templateName.maxLength"),
								},
							}}
							render={({ field: { onChange, value, ...otherFields }, fieldState }) => (
								<TextAreaWithCharLimit
									{...otherFields}
									data-test={e2eTestId.createTemplatePage.templateNameInput}
									autoComplete="off"
									autoFocus={!value}
									text={value}
									onTextChange={onChange}
									errorMessage={fieldState.error?.message}
									charLimit={APP_CONSTANTS.PROGRAM_OR_TEMPLATE_MAX_TITLE_LENGTH}
									placeholder={format("templateBuilder.templateNameDialog.nameInput.placeholder")}
								/>
							)}
						/>
					</FormField>
				</Form>
			</DialogContainer>
		</BaseDialog>
	);
};

const DialogContainer = styled.div`
	${stack("M", "flexstart", "stretch")}
	height: 100%;
`;

const ButtonContainer = styled.div`
	${row("S", "flexend")}
`;

const Form = styled(motion.form)`
	${stack("M", "spacebetween", "stretch")}
	height: 100%;
`;

const FormField = styled.div`
	${stack("S", "flexstart", "stretch")};
`;
